/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { type ReactNode, Suspense, useEffect, useRef } from 'react';

import { type GraphQLError } from 'graphql';
import { ErrorBoundary } from 'react-error-boundary';
import { useQueryLoader } from 'react-relay';

import { cssMap, cx, jsx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { FlagsProvider } from '@atlaskit/flag';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import {
	type AIEventsInstrumentationConfig,
	AIEventsInstrumentationProvider,
	useAIEventsInstrumentation,
} from '@atlassian/ai-analytics';
import { AIFeedbackPropertiesProvider, AIStreamLoading } from '@atlassian/ai-components';
import { RelayEnvironmentProvider } from '@atlassian/relay-environment-provider';
import {
	ExperiencePerformanceTypes,
	ExperienceTypes,
	useUFOExperience,
} from '@atlassian/ufo-experimental';

import { AISnippetContent } from '../../common/ui';
import type { AISnippetProps } from '../../types';

import { AISnippetLoader } from './ai-snippet-loader';
import AISnippetLoaderQuery, {
	type aiSnippetLoaderQuery,
	type KnowledgeGraphContentType,
} from './ai-snippet-loader/__generated__/aiSnippetLoaderQuery.graphql';

const SNIPPET_ENTRYPOINT_ID = '55dba3ae-58fa-41e0-828b-1e30fe66c0ba';
const ANALYTICS_FROM_CODE = 'SMARTLINK';
const DEFAULT_MAX_LINES = 4;

const styles = cssMap({
	container: {
		font: token('font.body.small'),
	},
});

const getContentTypeParam = (contentType: string): KnowledgeGraphContentType | null =>
	contentType === 'page' ? 'PAGE' : contentType === 'blogpost' ? 'BLOGPOST' : null;

const getAnalyticsConfig = (props: AISnippetProps): AIEventsInstrumentationConfig => ({
	product: 'confluence',
	subproduct: 'confluence',
	source: 'confluencePage',
	aiFeatureName: 'aiSnippet',
	proactiveGeneratedAI: 1,
	userGeneratedAI: 0,
	additionalAttributes: {
		contentId: props.contentId,
		contentType: props.contentType,
		from: props.from ?? ANALYTICS_FROM_CODE,
		productAttributionOverride: 'confluence',
	},
	enablePlatformEvents: true,
});

/**
 * Main AI Snippet component. Fetches the AI snippet data from the server and displays it.
 * If the snippet data type is not supported, it will display the fallback component.
 * If the AI snippet data is not found or another error occurs it will display the AISnippet component,
 * but with the fallback text in place of the response text.
 *
 * @param contentId - The content ID of the AI snippet
 * @param contentType - The content type of the AI snippet
 * @param cloudId - The cloud ID of the AI snippet
 * @param fallbackText - The text to display if the AI snippet fails to load
 * @param fallbackComponent - The component to display if the AI snippet is not supported
 * @param source [optional] - The source for where the AI snippet was triggered from defaults to 'smartlink'
 * @param onLoadComplete [optional] - The callback function that executes when loading has completed
 * @returns
 */
export const AISnippet = (props: AISnippetProps): JSX.Element => {
	return (
		<AIEventsInstrumentationProvider config={getAnalyticsConfig(props)}>
			<AISnippetWithAnalytics {...props} />
		</AIEventsInstrumentationProvider>
	);
};

export const AISnippetWithAnalytics = ({
	contentId,
	contentType,
	cloudId,
	fallbackText,
	fallbackComponent,
	userId,
	displayName,
	source = 'smartlink',
	maxLines = DEFAULT_MAX_LINES,
	isHidden = false,
	onLoadComplete,
	showFooter,
	showAwareness,
}: AISnippetProps):
	| string
	| number
	| boolean
	| JSX.Element
	| Iterable<ReactNode>
	| null
	| undefined => {
	const [queryRef, loadQuery] = useQueryLoader<aiSnippetLoaderQuery>(AISnippetLoaderQuery);
	const isLoadingRef = useRef(true);

	const { trackAIInteractionInit, trackAIResultError, trackAIInteractionDismiss } =
		useAIEventsInstrumentation();

	const { start, success, failure } = useUFOExperience({
		id: 'ai-snippet-load',
		component: 'ai-snippet',
		type: ExperienceTypes.Load,
		performanceType: ExperiencePerformanceTypes.InlineResult,
	});

	const contentTypeParam = getContentTypeParam(contentType);
	const isValidQuery = contentType && cloudId && contentId && contentTypeParam;
	const isEnabledForJira = fg('jira-cc-ai-snippet-gate');
	const isTrackingEnabled = FeatureGates.getExperimentValue(
		'cc-ai-snippet-experiment-tracking',
		'isEnabled',
		false,
	);

	useEffect(() => {
		if (!isHidden) {
			trackAIInteractionInit();
		}

		return () => {
			if (isLoadingRef.current && !isHidden) {
				trackAIInteractionDismiss();
			}
		};
		// Only on component mount/unmount
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		isLoadingRef.current = !queryRef;
		if (queryRef) {
			if (isTrackingEnabled) {
				success();
			}
			onLoadComplete?.();
		}
	}, [queryRef, onLoadComplete, success, isTrackingEnabled]);

	useEffect(() => {
		if (!isValidQuery) {
			// Skip query if we don't have valid params as we'll just return the fallback component
			return;
		}

		if (isTrackingEnabled) {
			start();
		}
		loadQuery({
			contentId,
			contentType: contentTypeParam!,
			cloudId,
			includeMe: isEnabledForJira,
		});
	}, [
		contentId,
		contentTypeParam,
		cloudId,
		loadQuery,
		isValidQuery,
		isEnabledForJira,
		start,
		isTrackingEnabled,
	]);

	if (!isValidQuery) {
		return fallbackComponent;
	}

	const content = (
		<ErrorBoundary
			fallback={
				<AISnippetContent
					text={fallbackText}
					contentId={contentId}
					showFooter={false}
					showAwareness={showAwareness}
					source={source}
					isHidden={isHidden}
					maxLines={maxLines}
				/>
			}
			onError={(error: Error) => {
				if (!isHidden) {
					if (isTrackingEnabled) {
						failure({
							metadata: {
								errorCode:
									'extensions' in error
										? (error as GraphQLError).extensions?.statusCode || 500
										: 999,
								errorMessage: error.message || 'Unknown error',
							},
						});
					}
					trackAIResultError({
						aiErrorCode:
							'extensions' in error ? (error as GraphQLError).extensions?.statusCode || 500 : 999,
						aiErrorMessage: error.message || 'Unknown error',
					});
				}
			}}
		>
			<Box xcss={cx(styles.container)}>
				<Suspense fallback={<AIStreamLoading />}>
					{queryRef ? (
						<AISnippetLoader
							queryRef={queryRef}
							fallbackText={fallbackText}
							contentId={contentId}
							source={source}
							isHidden={isHidden}
							maxLines={maxLines}
							showFooter={showFooter}
							showAwareness={showAwareness}
						/>
					) : (
						<AIStreamLoading />
					)}
				</Suspense>
			</Box>
		</ErrorBoundary>
	);

	return (
		<FlagsProvider>
			{isEnabledForJira ? (
				content
			) : (
				<AIFeedbackPropertiesProvider
					entrypointId={SNIPPET_ENTRYPOINT_ID}
					userId={userId}
					displayName={displayName}
				>
					{content}
				</AIFeedbackPropertiesProvider>
			)}
		</FlagsProvider>
	);
};

/**
 * This is a wrapper component that provides an Atlassian Relay environment to the AISnippet component.
 * Only use this if your app does not have a top level Relay environment provider already established.
 * @param props - See AISnippet component
 * @returns
 */
export const AISnippetWithRelayEnvironment = (props: AISnippetProps): JSX.Element => {
	return (
		<RelayEnvironmentProvider>
			<AISnippet {...props} />
		</RelayEnvironmentProvider>
	);
};
